import { ideApi } from '@/api/ideApi'
import { useIdeStore } from '@/store/ideStore'
import { toast } from 'sonner'

function buildAiPrompt(prompt, taskType, context) {
  const sections = []
  if (taskType && taskType !== 'chat') {
    sections.push(`Task type: ${taskType}`)
  }
  if (context.activeFile) {
    sections.push(`Active file: ${context.activeFile}`)
  }
  if (context.fileContents) {
    sections.push(`File snapshot:\n${context.fileContents}`)
  }
  if (context.terminalLogs) {
    sections.push(`Recent terminal logs:\n${context.terminalLogs}`)
  }
  sections.push(`User prompt:\n${prompt}`)
  return sections.join('\n\n')
}

export const aiService = {
  async sendPrompt(prompt, taskType = 'chat', aiAgent = 'claude') {
    const state = useIdeStore.getState()

    const activeFile = state.openFiles.find(file => file.path === state.activeFilePath)
    const context = {
      activeFile: state.activeFilePath,
      fileContents: activeFile ? activeFile.contents : null,
      terminalLogs: state.terminalHistory
        .slice(-5)
        .map(entry => `> ${entry.command}\n${entry.stdout}\n${entry.stderr}`)
        .join('\n'),
    }

    state.appendAiMessage({ role: 'user', content: prompt })
    try {
      const response = await ideApi.generateAi({
        prompt: buildAiPrompt(prompt, taskType, context),
        task_type: taskType,
        ai_agent: aiAgent.toLowerCase(),
      })
      const output = response?.data?.output || 'AI completed but returned no output.'

      if (typeof output === 'string' && output.startsWith('[openai:error]')) {
        let errorMsg = output.replace('[openai:error]', '').trim()
        try {
          const jsonStart = errorMsg.indexOf('{')
          if (jsonStart !== -1) {
            const jsonObj = JSON.parse(errorMsg.substring(jsonStart))
            if (jsonObj?.error?.message) {
              errorMsg = jsonObj.error.message
            }
          }
        } catch (e) {
          // Fallback to raw string
        }

        toast.error(errorMsg, {
          title: 'Arkitect AI Error',
          duration: 5000,
        })
        throw new Error(errorMsg)
      }

      state.appendAiMessage({ role: 'assistant', content: output })
      if (response?.data?.usage) {
        state.setUsageSnapshot(response.data.usage)
      } else {
        const usage = await ideApi.getUsage()
        state.setUsageSnapshot(usage.data)
      }
      state.setUpgradePrompt(null)
      return response
    } catch (error) {
      const message = String(error?.message || 'AI request failed.')
      if (
        message.toLowerCase().includes('upgrade') ||
        message.toLowerCase().includes('limit') ||
        message.toLowerCase().includes('quota')
      ) {
        state.setUpgradePrompt('Your current plan has reached its AI usage limit. Upgrade to continue.')
      }
      throw error
    }
  },
}
